/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { LogoutOutlined, UserOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import {
  teamIdentity,
  teamFullMessageFor,
} from '@sportninja/common/features/registration/startStep/teamDisplay'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { Empty } from 'antd'
import { EventCard } from 'src/components/RegistrationEventCard'
import Button from 'src/components/ts-components/Button'
import Text from 'src/components/ts-components/Text'
import WarningModal from 'src/components/WarningModal'
import WarningIcon from 'src/components/icons/WarningIcon'
import DivisionFilters from './components/DivisionFilters'
import EntityZone from './components/EntityZone'
import ResolvedRow from './components/ResolvedRow'
import RoleZone from './components/RoleZone'
import SelectionSummary from './components/SelectionSummary'
import ZoneSection from './components/ZoneSection'
import { useRegistrationStart } from './hooks/useRegistrationStart'

const ROLE_LABEL: Record<string, string> = {
  player: 'Player',
  team: 'Team',
}

/**
 * The merged registration start step.
 *
 * Replaces the two-screen `select-division` -> `select-role` opening with one
 * screen asking three questions in order — what you are registering, where, and
 * which of your people. Every question the URL or the data can already answer
 * collapses to a summary line instead of costing a screen, which is what keeps
 * three questions from reading as two screens stacked.
 *
 * Logged-out readers get the first two questions and a Continue that goes to
 * sign-up; they return to this same slug with the third question live. That
 * keeps divisions and prices visible before any sign-up wall.
 */
const RegistrationStart = () => {
  const s = useRegistrationStart()

  // A deep link pre-selects, it does not settle: the division and team it names
  // arrive highlighted in the ordinary picker, and the reader can change either
  // one. So "where" is always asked here — only the role zone can collapse.
  // Only ask the role when there is a genuine choice to make.
  const roleAsked = !s.soleRole && !s.nothingAvailable
  // The only reason a role is settled rather than asked. A `?type=` link
  // pre-selects but no longer disables the other side, so it never collapses
  // this zone — the reader is still being asked, with an answer filled in.
  const roleResolvedReason = s.soleRole
    ? 'the only option in this competition'
    : undefined

  // Number only the questions actually asked, so nobody sees a step 2 without
  // having been shown a step 1.
  let zone = 0
  const nextIndex = () => {
    zone += 1
    return zone
  }
  // A divider belongs to a boundary, not a position: the role zone may not
  // render at all, and the first thing on the page must not carry a rule above
  // nothing.
  const roleRendered = roleAsked || Boolean(s.roleKey)
  const roleIndex = roleAsked ? nextIndex() : 0
  const entityIndex = nextIndex()

  if (s.nothingAvailable) {
    return (
      <div css={containerStyle}>
        <EventCard eventInfo={s.registration} />
        <Empty
          description={
            <Text variant='paragraphMedium' color={colors.NEUTRAL_0}>
              Player And Team Registrations Are Currently Unavailable.
            </Text>
          }
          image={<WarningIcon color={colors.PRIMARY} size={80} />}
        />
      </div>
    )
  }

  return (
    <div css={containerStyle}>
      {s.email && s.isLogged ? (
        <div css={accountRowStyle}>
          <span css={accountStyle}>
            <UserOutlined css={primaryIconStyle} />
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.PRIMARY}
            >
              {s.email}
            </Text>
          </span>
          <button type='button' css={startOverStyle} onClick={s.handleLogout}>
            <LogoutOutlined css={dangerIconStyle} />
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.ERROR_200}
            >
              {capitalizeWords('Logout')}
            </Text>
          </button>
        </div>
      ) : null}

      <EventCard eventInfo={s.registration} />

      <div css={zonesStyle}>
        {/* 1 — what are you registering? */}
        {roleAsked ? (
          <ZoneSection
            index={roleIndex}
            title='Registering For'
            subtitle='Choose the option that best fits your role in this competition.'
            complete={Boolean(s.roleKey)}
          >
            <RoleZone
              value={s.roleKey}
              onChange={s.setRoleKey}
              playerAvailable={s.playerAvailable}
              teamAvailable={s.teamAvailable}
              teamFull={s.teamFull}
            />
          </ZoneSection>
        ) : s.roleKey ? (
          <ResolvedRow
            label='Registering'
            value={ROLE_LABEL[s.roleKey]}
            reason={roleResolvedReason}
          />
        ) : null}

        {/* 2 — where? */}
        <ZoneSection
          index={entityIndex}
          divided={roleRendered}
          title='Select a Team/Draft or Division'
          subtitle='Start your registration by selecting the appropriate division, team or draft.'
          complete={s.entityAnswered}
          inert={roleAsked && !s.roleKey}
        >
          <DivisionFilters
            ageFilters={s.ageFilters}
            genderFilters={s.genderFilters}
            dayOfWeekFilters={s.dayOfWeekFilters}
            onAgeChange={s.setSelectedAge}
            onGenderChange={s.setSelectedGender}
            onDayOfWeekChange={s.setSelectedDayOfWeek}
          />
          <EntityZone
            tab={s.tab}
            onTabChange={s.handleTabChange}
            onSearch={s.setQuery}
            query={s.query}
            viewMode={s.viewMode}
            onViewModeChange={s.setViewMode}
            teamTabDisabled={s.isTeamRole}
            isMobile={s.isMobile}
            divisions={s.divisions}
            selectedDivisionId={s.selectedDivision?.id ?? null}
            onSelectDivision={s.handleSelectDivision}
            currencyName={s.currencyCode}
            scrollToSelectionKey={s.scrollKey}
            teamResults={s.teamResults}
            selectedTeamId={teamIdentity(s.selectedTeam?.team) ?? null}
            onSelectTeam={s.handleSelectTeam}
            scopedDivisionName={
              s.selectedDivision ? s.selectedDivision.name : null
            }
            onClearScope={
              s.selectedDivision ? s.handleClearTeamScope : undefined
            }
            role={s.roleKey}
          />
        </ZoneSection>
      </div>

      {/* A `?team_id=` link named a team that is now full. Acknowledge-only:
          the step has already dropped the param and scoped the list to that
          team's division, so there is nothing here to accept or decline —
          only something the reader needs to know before they choose again. */}
      <WarningModal
        isOpen={Boolean(s.fullLinkedTeamName)}
        onClose={s.dismissFullLinkedTeam}
        onConfirm={s.dismissFullLinkedTeam}
        title={capitalizeWords('Team Is Full')}
        description={teamFullMessageFor(s.fullLinkedTeamName)}
        confirmLabel='OK'
        hideCancel
      />

      {/* Only once both questions are answered. Who is being registered is
          asked on the next step, beside the form describing them. */}
      {(s.isLogged ? s.canSubmit : s.canContinue) ? (
        <>
          {/* The footer is fixed, so it sits over the content rather than in
              the flow. This reserves the height it covers, so the last card in
              the grid can still be scrolled clear of it. */}
          <div css={footerSpacerStyle} aria-hidden='true' />
          <div css={footerStyle}>
            {/* What they are committing to, beside the button that commits it.
                A team-first reader never answered the division separately, so
                the division is read off the team that carries it. */}
            <SelectionSummary
              role={s.roleKey}
              team={s.selectedTeam?.team}
              divisionName={
                s.selectedDivision?.name ?? s.selectedTeam?.division?.name
              }
            />

            <div css={actionColumnStyle}>
              {/* Button sets `flex: 1 0 0` on itself, so as a direct child of
                  a column it would grow to fill the footer's height. This
                  wrapper gives it a block to size against instead. */}
              <div css={buttonWrapStyle}>
                <Button
                  onClick={s.handleNext}
                  text={s.isLogged ? 'NEXT' : 'CONTINUE'}
                  // The footer only renders when the action is available, so
                  // the only thing left to guard is a second press while the
                  // first is in flight.
                  disabled={s.isSubmitting}
                  isLoading={s.isSubmitting}
                  variant='primary'
                  style={buttonStyle}
                />
              </div>
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.WHITE}
                style={'text-align: right;'}
              >
                * Applicable taxes will be applied to all payments
              </Text>
            </div>
          </div>
        </>
      ) : null}
    </div>
  )
}

export default RegistrationStart

const containerStyle = css`
  display: flex;
  flex-direction: column;
  flex: 1;
  background-color: ${colors.NEUTRAL_800};
  width: 1080px;
  padding: 0 24px 24px 24px;
  border-radius: 16px 16px 0 0;

  @media (max-width: 768px) {
    min-width: 0;
    width: 100%;
  }
`

const accountRowStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  margin-top: 16px;
  margin-bottom: -8px;
`

const accountStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
`

const startOverStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
`

const primaryIconStyle = css`
  color: ${colors.PRIMARY};
`

const dangerIconStyle = css`
  color: ${colors.ERROR_200};
`

const zonesStyle = css`
  display: flex;
  flex-direction: column;
  /* Half the separation between steps; the divided step's own padding-top
     supplies the other half, which leaves the rule centred in the gap rather
     than crowded against the step below it. */
  gap: 24px;
  width: 100%;
  /* 24px below the event header, matching the old screen's PageTitle. */
  margin-top: 24px;
`

/* Reserves the height the fixed footer covers, so the last row of cards can be
   scrolled out from under it. Matches the footer's own min-height. */
const footerSpacerStyle = css`
  /* Taller than the footer's own 150px min-height on purpose. Matching it
     exactly left the last card flush against the gradient's opaque band —
     technically clear of the button, but with nothing between them. The extra
     is breathing room, so the end of the list reads as the end. */
  height: 200px;
  flex: none;
`

/* Pinned to the bottom of the viewport, matching the old select-division step:
   the action stays reachable however far down the grid the reader has scrolled,
   instead of being a destination they have to find.

   The gradient fades the page out beneath it rather than cutting a hard edge,
   so cards passing underneath read as continuing rather than clipped. Fixed
   positioning takes the element out of the flow, so the width is stated here
   rather than inherited from the 1080px container. */
const footerStyle = css`
  position: fixed;
  bottom: 0;
  /* Centred against the VIEWPORT, matching the page's own centred column. A
     fixed element with no left offset keeps its static-position left edge —
     which is already inset by the container's padding — and would then run its
     full width from there, overshooting the right side. */
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  width: 1080px;
  max-width: 100%;
  min-height: 150px;
  padding: 32px 35px 16px 35px;
  /* A row: what is being registered on the left, the action on the right. They
     stack on a narrow viewport rather than squeezing the summary. */
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  background: linear-gradient(
    0deg,
    ${colors.NEUTRAL_700} 37.24%,
    rgba(51, 54, 59, 0) 100%
  );

  @media (max-width: 768px) {
    width: 100%;
    padding: 32px 24px 16px 24px;
  }
`

/* The button and its taxes note, kept together and right-aligned. `flex: none`
   so the summary beside it gives way first when the line is tight. */
const actionColumnStyle = css`
  display: flex;
  flex: none;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
`

/* Holds the button at its own size inside the action column. Without it the
   button's own `flex: 1 0 0` stretches it to the column's full height. */
const buttonWrapStyle = css`
  flex: none;
  display: flex;
  width: 200px;

  @media (max-width: 768px) {
    width: 100%;
  }
`

const buttonStyle = css`
  width: 100%;
`
